import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NavLink } from "react-router";
import { can } from "@/shared/auth/roles";
import { useOperatorSession } from "@/shared/auth/session";
import { NAV_ITEMS, type NavItem } from "@/shared/constants/nav";
import { ROUTES } from "@/shared/constants/routes";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { Brand } from "./Brand";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Drawer mode (under 1024 px): full width, no collapse control. */
  drawer?: boolean;
  onNavigate?: (() => void) | undefined;
  className?: string | undefined;
}

function NavEntry({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const node = (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex h-9 items-center gap-2.5 rounded-md px-2.5 text-cell font-medium transition-colors duration-120 no-underline",
          collapsed && "justify-center px-0",
          isActive ? "bg-accent-soft text-accent-fg" : "text-fg-muted hover:bg-surface-hover hover:text-fg",
        )
      }
    >
      <Icon icon={item.icon} size={16} />
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
    </NavLink>
  );
  if (!collapsed) return <li>{node}</li>;
  return (
    <li>
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    </li>
  );
}

/** Generated from NAV_ITEMS with role gates (rules/01 §10): admin-only sections are hidden from operators. */
export function Sidebar({ collapsed, onToggle, drawer = false, onNavigate, className }: SidebarProps) {
  const { operator } = useOperatorSession();
  const visible = NAV_ITEMS.filter((item) => !item.adminAction || can(operator.role, item.adminAction));
  const isCollapsed = collapsed && !drawer;
  return (
    <nav
      aria-label="Main"
      className={cn(
        "flex h-full shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200 ease-out",
        isCollapsed ? "w-14" : "w-60",
        className,
      )}
    >
      <div
        className={cn(
          "flex h-13 items-center border-b border-border",
          isCollapsed ? "justify-center" : "px-4",
        )}
      >
        <NavLink to={ROUTES.tenants} className="no-underline" aria-label="Go to tenants" onClick={onNavigate}>
          <Brand compact={isCollapsed} />
        </NavLink>
      </div>
      <ul className={cn("flex flex-1 flex-col gap-0.5 py-3", isCollapsed ? "px-2" : "px-3")}>
        {visible.map((item) => (
          <NavEntry key={item.id} item={item} collapsed={isCollapsed} onNavigate={onNavigate} />
        ))}
      </ul>
      {!drawer ? (
        <div className={cn("border-t border-border py-2", isCollapsed ? "px-2" : "px-3")}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn("w-full", isCollapsed ? "justify-center px-0" : "justify-start")}
          >
            <Icon icon={isCollapsed ? PanelLeftOpen : PanelLeftClose} size={16} />
            {!isCollapsed ? "Collapse" : null}
          </Button>
        </div>
      ) : null}
    </nav>
  );
}
